// src/pages/gene.codes/bloods/BloodAnalysiscomponents/BloodTestWheel.tsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import * as d3 from "d3";
import {  PieChartOutlined } from "@ant-design/icons";
import { BloodTestResult } from "../../../../types/lab/BloodTestTypes";
import { calculateResultSeverity } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";

interface BloodTestWheelProps {
	results: BloodTestResult[];
	category: string;
	rangeContext: RangeContext;
	onResultClick: (result: BloodTestResult) => void;
	selectedResult: BloodTestResult | null;
	width?: number;
	height?: number;
}

interface WheelNode {
	id: string;
	name: string;
	value: number;
	result?: BloodTestResult;
	flag?: string;
	children?: WheelNode[];
	depth?: number;
	x0?: number;
	x1?: number;
	y0?: number;
	y1?: number;
}

const BloodTestWheel: React.FC<BloodTestWheelProps> = ({ results, category, rangeContext, onResultClick, selectedResult, width = 400, height = 400 }) => {
	const svgRef = useRef<SVGSVGElement>(null);
	const wheelGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
	const centerGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
	const [currentRotation, setCurrentRotation] = useState(0);
	// Live rotation angle. Click handlers are bound once in renderWheel, so they
	// can't see updated state — the ref gives animateRotation the real current
	// angle to rotate *from*, instead of always restarting from the stale initial state.
	const currentRotationRef = useRef(0);
	const [activeNode, setActiveNode] = useState<WheelNode | null>(null);

	const radius = useMemo(() => {
		return Math.min(width, height) / 2 - 20;
	}, [width, height]);

	const wheelData = useMemo(() => {
		if (!results || results.length === 0) return null;

		const rootNode: WheelNode = {
			id: `category-${category}`,
			name: category,
			value: results.length,
			children: [],
		};

		results.forEach((result) => {
			const resultNode: WheelNode = {
				id: `result-${result.id}`,
				name: result.test_name,
				value: 1,
				result: result,
				flag: calculateResultSeverity(result, rangeContext).flag ?? undefined,
			};
			rootNode.children!.push(resultNode);
		});

		return rootNode;
	}, [results, category, rangeContext]);

	const getEffectiveAngle = (originalAngle: number, currentRotation: number): number => {
		const rotationRadians: number = (currentRotation * Math.PI) / 180;
		let effectiveAngle: number = originalAngle + rotationRadians;
		while (effectiveAngle < 0) effectiveAngle += 2 * Math.PI;
		while (effectiveAngle >= 2 * Math.PI) effectiveAngle -= 2 * Math.PI;
		return effectiveAngle;
	};

	interface TextNode extends d3.HierarchyRectangularNode<WheelNode> {
		x0: number;
		x1: number;
	}

	interface TextSelection extends d3.Selection<SVGTextElement, TextNode, SVGGElement, unknown> {}

	const updateTextOrientation = (textSelection: TextSelection, currentRotation: number): void => {
		textSelection.attr("transform", (d: TextNode): string => {
			const angle: number = (d.x0 + d.x1) / 2;
			const textRadius: number = radius * 0.65;

			const effectiveAngle: number = getEffectiveAngle(angle, currentRotation);
			const shouldFlip: boolean = effectiveAngle > Math.PI;

			const textAngle: number = (angle * 180) / Math.PI - 90;
			const flipRotation: number = shouldFlip ? 180 : 0;

			return `rotate(${textAngle}) translate(${textRadius}, 0) rotate(${flipRotation})`;
		});
	};

	const getResultColor = (result?: BloodTestResult) => {
		if (!result) return "#f8fafc";
		const severityData = calculateResultSeverity(result, rangeContext);
		return severityData.color;
	};

	const getLighterColor = (result?: BloodTestResult) => {
		if (!result) return "#ffffff";
		const severityData = calculateResultSeverity(result, rangeContext);
		return severityData.lightColor;
	};

	const renderWheel = () => {
		if (!svgRef.current || !wheelData) return;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		svg.attr("viewBox", `0 0 ${width} ${height}`);

		const wheelGroup = svg
			.append("g")
			.attr("transform", `translate(${width / 2}, ${height / 2}) rotate(${currentRotation})`)
			.attr("class", "wheel-group");

		wheelGroupRef.current = wheelGroup;

		const centerGroup = svg
			.append("g")
			.attr("transform", `translate(${width / 2}, ${height / 2})`)
			.attr("class", "center-group");

		centerGroupRef.current = centerGroup;

		const partition = d3.partition<WheelNode>().size([2 * Math.PI, radius]);

		const root = d3.hierarchy(wheelData).sum((d) => (d.children ? 0 : d.value || 1));

		const partitionedRoot = partition(root);

		const arc = d3
			.arc<d3.HierarchyRectangularNode<WheelNode>>()
			.startAngle((d) => d.x0)
			.endAngle((d) => d.x1)
			.innerRadius((d) => (d.depth === 0 ? 0 : radius * 0.3))
			.outerRadius((d) => (d.depth === 0 ? radius * 0.3 : radius));

		const visibleNodes = partitionedRoot.descendants();

		const paths = wheelGroup
			.selectAll("path")
			.data(visibleNodes)
			.enter()
			.append("path")
			.attr("d", arc as any)
			.attr("fill", (d) => {
				if (d.depth === 0) {
					return "#f8fafc";
				}
				return getResultColor(d.data.result);
			})
			.attr("stroke", "#ffffff")
			.attr("stroke-width", 1.5)
			.attr("class", (d) => `segment segment-${d.data.id} ${activeNode?.id === d.data.id ? "active" : ""}`)
			.style("cursor", (d) => (d.depth > 0 ? "pointer" : "default"))
			.on("click", (event, d) => {
				if (d.depth === 0) return;
				event.stopPropagation();

				const angle = (d.x0 + d.x1) / 2;
				const targetRotation = -(((angle - Math.PI / 2) * 180) / Math.PI);
				animateRotation(targetRotation);

				setActiveNode(d.data);
				if (d.data.result) {
					onResultClick(d.data.result);
				}
			})
			.on("mouseover", function (event, d) {
				if (d.depth === 0) return;
				d3.select(this).transition().duration(150).attr("fill", getLighterColor(d.data.result)).attr("stroke-width", 2);
			})
			.on("mouseout", function (event, d) {
				if (d.depth === 0) return;
				d3.select(this).transition().duration(150).attr("fill", getResultColor(d.data.result)).attr("stroke-width", 1.5);
			});

		const texts = wheelGroup
			.selectAll("text")
			.data(visibleNodes.filter((d) => d.depth > 0))
			.enter()
			.append("text")
			.attr("text-anchor", "middle")
			.attr("dominant-baseline", "central")
			.attr("class", "result-label wheel-label")
			.style("font-size", "10px")
			.style("font-weight", "500")
			.style("fill", "#1f2937")
			.style("pointer-events", "none")
			.text((d) => {
				const maxChars = 25;
				const name = d.data.name;
				if (name.length <= maxChars) return name;
				return name.substring(0, maxChars - 3) + "...";
			});

		updateTextOrientation(texts, currentRotation);

		centerGroup
			.append("text")
			.attr("text-anchor", "middle")
			.attr("dominant-baseline", "central")
			.attr("class", "center-label")
			.attr("font-size", "12px")
			.attr("font-weight", "600")
			.attr("fill", "#374151")
			.text(() => {
				const maxChars = 18;
				const name = category;
				if (name.length <= maxChars) return name;
				return name.substring(0, maxChars - 3) + "...";
			});

		centerGroup.append("text").attr("text-anchor", "middle").attr("dominant-baseline", "central").attr("dy", "1.2em").attr("class", "count-label").attr("font-size", "10px").attr("fill", "#6b7280").text(`${results.length} tests`);
	};

	const animateRotation = (targetRotation: number, duration: number = 800) => {
		if (!wheelGroupRef.current) return;

		const startRotation = currentRotationRef.current;
		wheelGroupRef.current
			.transition()
			.duration(duration)
			.ease(d3.easeCubicInOut)
			.attrTween("transform", () => {
				const interpolate = d3.interpolate(startRotation, targetRotation);
				return (t: number) => {
					const currentRot = interpolate(t);
					// Track the live angle each frame so an interrupting click rotates
					// from the current position instead of snapping back.
					currentRotationRef.current = currentRot;
					if (t === 1) {
						setCurrentRotation(targetRotation);
						requestAnimationFrame(() => {
							if (wheelGroupRef.current) {
								updateTextOrientation(wheelGroupRef.current.selectAll("text.wheel-label"), targetRotation);
							}
						});
					}
					return `translate(${width / 2}, ${height / 2}) rotate(${currentRot})`;
				};
			});
	};

	const resetRotation = () => {
		animateRotation(0);
		setActiveNode(null);
	};

	useEffect(() => {
		renderWheel();
	}, [wheelData, width, height]);

	useEffect(() => {
		if (wheelGroupRef.current) {
			requestAnimationFrame(() => {
				if (wheelGroupRef.current) {
					updateTextOrientation(wheelGroupRef.current.selectAll("text.wheel-label"), currentRotation);
				}
			});
		}
	}, [currentRotation]);

	useEffect(() => {
		if (selectedResult && wheelData) {
			const selectedNode = wheelData.children?.find((child) => child.result?.id === selectedResult.id);
			setActiveNode(selectedNode || null);
		}
	}, [selectedResult, wheelData]);

	if (!wheelData || results.length === 0) {
		return (
			<div className="flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded" style={{ width, height, minHeight: "200px" }}>
				<div className="text-center">
					<PieChartOutlined className="text-4xl text-gray-300 dark:text-gray-600 mb-3" />
					<h6 className="text-gray-500 dark:text-gray-400 m-0">No Results</h6>
					<p className="text-gray-400 dark:text-gray-500 text-xs m-0 mt-1">No test results available for this category</p>
				</div>
			</div>
		);
	}

	return (
		<div className="blood-test-wheel relative">
			<div className="relative">
				<svg ref={svgRef} width={width} height={height} className="blood-wheel-svg" style={{ maxWidth: "100%", height: "auto" }} />
			</div>

			<style>{`
				.blood-test-wheel .segment {
					transition: all 0.15s ease;
				}

				.blood-test-wheel .segment:hover {
					filter: brightness(1.1);
				}

				.blood-test-wheel .segment.active {
					stroke-width: 3px;
					stroke: #3b82f6;
					filter: brightness(1.05);
				}

				.blood-test-wheel .result-label,
				.blood-test-wheel .center-label,
				.blood-test-wheel .count-label {
					font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif;
				}

				.dark .blood-test-wheel .center-label {
					fill: #e5e7eb;
				}

				.dark .blood-test-wheel .count-label {
					fill: #9ca3af;
				}

				.dark .blood-test-wheel .result-label {
					fill: #f3f4f6;
				}

				.dark .blood-test-wheel .segment[fill="#f8fafc"] {
					fill: #1f2937;
				}

				.dark .blood-test-wheel .segment {
					stroke: #374151;
				}
			`}</style>
		</div>
	);
};

export default BloodTestWheel;
